import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { isEqual, debounce, isEmpty } from 'underscore';

import { withDefaultStyles } from '@bingads-webui-react/with-default-styles';
import { ResponsiveMode } from '@bingads-webui-campaign-react/smart-component-util';
import { useClientContext } from '@bingads-webui-campaign-react/smart-page-context';
import { withErrorView } from '@bingads-webui-campaign-react/smart-page-error-boundary';
import { LogoImage } from '@bingads-webui-campaign-react/smart-component-image';

import { HamburgerMenu } from './components/hamburger-menu';
import { MenuList } from './components/menu-list';
import { ButtonList } from './components/button-list';
import { dataProperties } from './data-config';

const getStyles = (components, { palette = {}, effects = {} }) => {
  const container = {
    ...components.section,
    display: 'flex',
    alignItems: 'center',
    paddingTop: 10,
    marginBottom: 25,
  };

  return ({
    container,
    containerMobile: {
      // use padding instead of margin, so when calculate the panel/hamburger position
      // we can get a precise top
      padding: '12px 0 0 0',
      marginTop: 'unset !important',
    },
    logoContainer: {
      display: 'inline-block',
      width: 'fit-content',
      maxWidth: '50%',
      flex: 1,
    },
    logoContainerMobile: {
      marginLeft: 12,
      maxWidth: 'calc(100% - 56px)',
    },
    navContainer: {
      marginRight: 10,
      marginLeft: 'auto',
    },
    navListContainer: {
      listStyle: 'none',
      display: 'inline-block',
      margin: 0,
    },
    navItem: {
      fontStyle: 'normal',
      fontWeight: 'normal',
      lineHeight: '14px',
      float: 'left',
      paddingLeft: 48,
    },
    navLink: {
      textDecoration: 'underline',
      textUnderlinePosition: 'under',
      ...components.navLink,
      fontSize: 20,
      lineHeight: '28px',
      fontWeight: '600',
    },
    buttonList: {
      margin: 0,
      padding: 0,
    },
    buttonListFull: {
      marginLeft: 'auto', // this happens when no navigatin menu
    },
    buttonListMobile: {
      margin: 0,
      padding: 0,
      display: 'flex',
      justifyContent: 'space-around',
      position: 'absolute',
      top: '50%',
      transform: 'translateY(-50%)',
      width: '100%',
    },
    buttonItem: {
      display: 'inline-block',
      margin: '0px 10px',
      verticalAlign: 'bottom',
    },
    mobileFooter: {
      position: 'fixed',
      bottom: 0,
      zIndex: 2,
      backgroundColor: palette.primaryBackground,
      textAlign: 'center',
      boxShadow: effects.elevation,
      height: components.fixedMobileFooter.height,
    },
  });
};

export const UnStyledSmartHeaderView = ({
  classes,
  id,
  items,
  showItems,
  actionButtons,
  logoImageId,
  logoName,
  disabled,
  footerSplit,
}) => {
  const { isMobile, responsiveMode } = useClientContext();
  const isSmallDesktop = responsiveMode === ResponsiveMode.SmallDesktop;

  const [style, setStyle] = React.useState({});
  const [resized, setResized] = React.useState(false);

  const buttonList = (actionButtons.length > 0) ?
    (
      <ButtonList
        compactMode={!isMobile && !isSmallDesktop && showItems}
        actionButtons={actionButtons}
        buttonListClassName={classnames(
          (isMobile || isSmallDesktop) ? classes.buttonListMobile : classes.buttonList,
          !showItems && classes.buttonListFull
        )}
        buttonItemClassName={classes.buttonItem}
        useIconButton={isMobile || isSmallDesktop}
        disabled={disabled}
      />
    ) : null;

  // Ele might not have the final position yet, delay it to get the correct rect.
  const measuredRef = debounce((node) => {
    if (node !== null) {
      const rect = node.getBoundingClientRect();

      const newStyle = {
        width: rect.width,
        top: rect.top,
        left: rect.left,
        right: rect.right,
      };

      if (!isEqual(newStyle, style)) {
        setStyle(newStyle);
      }
    }
  }, 50);

  useEffect(() => {
    const resizeHandler = debounce(() => setResized(!resized), 50);
    window.addEventListener('resize', resizeHandler);

    return () => window.removeEventListener('resize', resizeHandler);
  }, [resized]);

  return (
    <React.Fragment>
      {
        !footerSplit &&
        <header
          id={id}
          ref={measuredRef}
        >
          <div
            className={classnames(
              classes.container,
              isMobile && classes.containerMobile
            )}
          >
            <div
              className={classnames(
                classes.logoContainer,
                'smart-page-logo-container',
                (isMobile || isSmallDesktop) && classes.logoContainerMobile
              )}
            >
              <LogoImage
                imageId={logoImageId}
                label={logoName}
                logoDataProperty={dataProperties.logo}
                businessNameDataProperty={dataProperties.businessName}
              />
            </div>
            {
              showItems && (isMobile || isSmallDesktop) &&
              <HamburgerMenu
                items={items}
                top={style.top}
                left={style.left}
                width={style.width}
                triggerRecalculation={() => setResized(!resized)}
              />
            }
            {
              showItems && !isMobile && !isSmallDesktop &&
              <MenuList
                navClassName={classes.navContainer}
                className={classes.navListContainer}
                navItemClassName={classes.navItem}
                navAnchorClassName={classes.navLink}
                items={items}
              />
            }
            {
              !isMobile && !isSmallDesktop && buttonList
            }
          </div>
        </header>
      }
      {
        isMobile && footerSplit && !isEmpty(actionButtons) &&
        <div
          id={id}
          ref={measuredRef}
        >
          <div
            className={classnames(classes.mobileFooter, 'smart-page-preview-mobile-footer')}
            style={{ width: style.width, left: style.left }}
          >
            {buttonList}
          </div>
        </div>
      }
    </React.Fragment>
  );
};

UnStyledSmartHeaderView.propTypes = {
  classes: PropTypes.shape({
    navContainer: PropTypes.string.isRequired,
    navItem: PropTypes.string.isRequired,
  }).isRequired,
  id: PropTypes.string.isRequired,
  items: PropTypes.arrayOf(PropTypes.shape({
    title: PropTypes.string.isRequired,
    targetId: PropTypes.string.isRequired,
  })),
  showItems: PropTypes.bool.isRequired,
  actionButtons: PropTypes.arrayOf(PropTypes.shape({
    actionType: PropTypes.string.isRequired,
  })),
  logoImageId: PropTypes.string,
  logoName: PropTypes.string.isRequired,
  disabled: PropTypes.bool,
  footerSplit: PropTypes.bool,
};

UnStyledSmartHeaderView.defaultProps = {
  items: [],
  actionButtons: [],
  logoImageId: null,
  disabled: false,
  footerSplit: false,
};

export const SmartHeaderView = withErrorView(withDefaultStyles(
  UnStyledSmartHeaderView,
  getStyles
), 'componentMode');
